import { dashboardPersistReduxActions } from "@/redux/actions/dashboardPersist";
import { APP_ROUTES, returnToLastUrl, useAppDispatch, useAppSelector, useAppTranslation, userManager } from "@/utils";
import type { SDKRequestInit } from "@payping/api";
import { CuToman, FiArrowRight } from "@payping/icons";
import { bnplConsumerGetPlanList, loggedOut } from "@payping/redux/actions";
import {
  Button,
  Collapse,
  Divider,
  Flex,
  Layout,
  Typography,
  theme,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import * as Sentry from "@sentry/react";
import { useNavigate, useSearchParams } from "react-router";
import Loading from "@/pages/loading";
import { GuaranteeType } from "@payping/api/app/api/bnplConsumerTypes";
import { useState } from "react";
import { URLS } from "@payping/utilities-shared/consts";
import PayUnAvailable from "../Unavailable";

const PaySetPlanSalrayPhoneNotFound = () => {
  const { t } = useAppTranslation();
  const [searchParams] = useSearchParams();
  const trackingCode = searchParams.get("trackingCode") || "";
  const planId = searchParams.get("planId") || "-111111111111";
  const shopInfo = useAppSelector((state) => state.dashboardPersist.shopInfo);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [currentOpenPlan, setCurrentOpenPlan] = useState<string>();

  const handleBack = () => {
    localStorage.clear();
    sessionStorage.clear();

    // remove sentry data
    Sentry.getCurrentScope().setUser(null);

    userManager.signoutCallback();
    dispatch(loggedOut());
    if (!shopInfo.url) {
      returnToLastUrl();
      return;
    }
    window.location.href = shopInfo.url;
  };

  const getPlansState = useMessagedAsyncRetry(
    async () => {
      try {
        const res = await dispatch(bnplConsumerGetPlanList(trackingCode, { isRaw: true } as SDKRequestInit));
        if (res.merchantName && res.merchantRedirectUrl) {
          dispatch(dashboardPersistReduxActions.setShopInfo(res.merchantName, res.merchantRedirectUrl));
        }

        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [dispatch, trackingCode],
    {
      success: false,
      error: true,
    },
  );

  const plans = getPlansState.value?.plansInfo.filter((i) => i.planId?.toString() !== planId);

  if (getPlansState.loading) {
    return <Loading />;
  }

  if (getPlansState.error) {
    return (
      <PayUnAvailable
        onRetry={() => {
          getPlansState.retry();
        }}
        onRetryLoading={getPlansState.loading}
        shopName={t("common.shop")}
        backToShop={() => {
          window.history.back();
        }}
      />
    );
  }

  const handleShowGuaranteeType = (type: GuaranteeType) => {
    if (type === GuaranteeType.Cheque) return t("common.guaranteeTypes.cheque");
    if (type === GuaranteeType.None) return t("common.guaranteeTypes.none");
    if (type === GuaranteeType.Salary) return t("common.guaranteeTypes.salary");

    return t("common.guaranteeTypes.promissory");
  };

  const handleClickChoosePlan = (pId: string) => {
    navigate(`${APP_ROUTES.pay.setPlan.url}?trackingCode=${trackingCode}&planId=${pId}`);
  };

  return (
    <Flex className="tw:w-full" vertical>
      <Button
        onClick={() => {
          handleBack();
        }}
        type="text"
        block
        className="tw:h-16 tw:min-h-16 tw:w-full tw:justify-start"
      >
        <FiArrowRight width={16} height={16} />
        <Typography.Text>{t("common.returnToShop")}</Typography.Text>
      </Button>
      <Divider className="tw:m-0!" />
      <Flex className="tw:w-full tw:px-4! tw:py-11!" gap={"middle"} vertical justify="center" align="center">
        <img
          className="tw:max-w-72"
          src={`${URLS.STORAGE_URL}/bnpl-consumer/credit-score-check.svg`}
          alt={"credit-score-check"}
        />
        <Typography.Title level={5} className="tw:m-0!">
          {t("pay.setPlan.salary.notFoundNumber.title")}
        </Typography.Title>
        <Typography.Text>{t("pay.setPlan.salary.notFoundNumber.content")}</Typography.Text>
        <Flex className="tw:w-full" vertical gap={"middle"}>
          {plans?.map((item, index) => (
            <Collapse
              key={item.planId ?? index}
              items={[
                {
                  headerClass: "tw:border-none!",
                  showArrow: false,
                  children: (
                    <div>
                      <Layout>
                        <Flex vertical className="tw:p-2!" gap={"small"}>
                          <Flex align="center" justify="space-between">
                            <Typography.Text type="secondary">{t("pay.prePayment")}</Typography.Text>
                            <Flex align="center" gap={"2px"}>
                              <Typography.Text>{`${formatLocalizedNumber({ value: item.prepayment ?? 0 })}`}</Typography.Text>
                              <CuToman width={24} height={24} />
                            </Flex>
                          </Flex>
                          <Flex align="center" justify="space-between">
                            <Flex align="center" gap={"2px"}>
                              <Typography.Text>{`${formatLocalizedNumber({ value: item.consumerOperationCost ?? 0 })}`}</Typography.Text>
                              <CuToman width={24} height={24} />
                            </Flex>
                          </Flex>
                          <Flex align="center" justify="space-between">
                            <Typography.Text type="secondary">{t("pay.amountOfEachInstallment")}</Typography.Text>
                            <Flex align="center" gap={"2px"}>
                              <Typography.Text>{`${formatLocalizedNumber({ value: item.eachInstallmentPayment ?? 0 })}`}</Typography.Text>
                              <CuToman width={24} height={24} />
                            </Flex>
                          </Flex>
                          <Flex align="center" justify="space-between">
                            <Typography.Text type="secondary">{t("pay.refundTotal")}</Typography.Text>

                            <Flex align="center" gap={"2px"}>
                              <Typography.Text>{`${formatLocalizedNumber({ value: item.totalFinalPayment ?? 0 })}`}</Typography.Text>
                              <CuToman width={24} height={24} />
                            </Flex>
                          </Flex>
                        </Flex>
                      </Layout>
                      <Flex align="center" justify="space-between" className="tw:mt-4!">
                        <Flex flex={1} vertical>
                          <Typography.Text type="secondary" className="tw:text-xs!">
                            {t("common.guarantee")}
                          </Typography.Text>
                          <Typography.Text> {handleShowGuaranteeType(item.guaranteeType ?? 0)}</Typography.Text>
                        </Flex>
                        <Flex align="center" justify="space-between" flex={1}>
                          <Button
                            type="primary"
                            block
                            onClick={() => {
                              handleClickChoosePlan(item.planId?.toString() ?? "");
                            }}
                          >
                            {t("common.choosePlan")}
                          </Button>
                        </Flex>
                      </Flex>
                    </div>
                  ),
                  key: item.planId?.toString(),
                  label: (
                    <Flex align="center" justify="space-between">
                      <Typography.Text>{`${formatLocalizedNumber({ value: item.creditAmount ?? 0 })} ${t("common.creditTomans")}`}</Typography.Text>
                      <Typography.Text type="secondary">
                        {t("common.months", { month: item.installmentsCount ?? 0 })}
                      </Typography.Text>
                    </Flex>
                  ),
                },
              ]}
              activeKey={[currentOpenPlan ?? ""]}
              onChange={() => {
                if (item.planId?.toString() !== currentOpenPlan) {
                  setCurrentOpenPlan(item.planId?.toString());
                }
              }}
              className="tw:flex tw:flex-col tw:gap-4! tw:bg-transparent!"
              style={{
                borderColor: token.colorBorder,
              }}
            ></Collapse>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default PaySetPlanSalrayPhoneNotFound;
